import axios from 'axios'
import constants from '../constants'
import { getAuthorizationHeader } from './_token'
import store from '../store'
import { LOGOUT } from '../reducers/user'
import { joinPaths } from 'utils/helpers'

export const apiURL = constants.API_URL
export const apiVersion = constants.API_VERSION
export const crURL = constants.CR_URL
export const appTimeout = parseInt(constants.APP_TIMEOUT, 10)

const baseURL = joinPaths(apiURL, apiVersion)
export const api = axios.create({
  baseURL,
  timeout: appTimeout,
  transformRequest: [
    function (data, headers) {
      if (headers.Authorization) {
        // Authorization header is already set
        return JSON.stringify(data)
      }

      const authorization = getAuthorizationHeader()
      const partnerId = store.getState()?.user?.partnerId

      if (authorization) {
        headers['Authorization'] = authorization
      } else {
        // check anonymous-session
        const anonymousSession = JSON.parse(sessionStorage.getItem('anonymous-session') || '{}')
        if ('access_token' in anonymousSession) {
          headers['X-Auth-Code'] = anonymousSession['access_token']
        }
      }

      if (partnerId) {
        headers['X-Preferred-Partner-Id'] = partnerId
      }
      return JSON.stringify(data)
    },
  ],
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.response.use(null, (error) => {
  if (error && error.response && error.response.status === 401) {
    store.dispatch({ type: LOGOUT })
  }
  return Promise.reject(error)
})

export const cr_api = axios.create({
  baseURL: crURL,
  timeout: appTimeout,
  crossdomain: true,
  transformRequest: [
    function (data, headers) {
      const authorization = getAuthorizationHeader()
      const partnerId = store.getState()?.user?.partnerId

      if (authorization) {
        headers['Authorization'] = authorization
      }
      if (partnerId) {
        headers['X-Preferred-Partner-Id'] = partnerId
      }
      return JSON.stringify(data)
    },
  ],
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
})

export async function makeApiRequest(
  targetApi = api,
  method,
  endpoint,
  requestData,
  { signal = null, cancelToken = null, verbose = true, accessToken, partnerId, timeout } = {},
) {
  timeout = timeout ?? appTimeout
  try {
    const headers = {}
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    } else {
      const partnerSession = JSON.parse(sessionStorage.getItem('partner-session') || '{}')
      if ('access_token' in partnerSession) {
        headers['X-Partner-Token'] = partnerSession['access_token']
      } else {
        const anonymousSession = JSON.parse(sessionStorage.getItem('anonymous-session') || '{}')
        if ('access_token' in anonymousSession) {
          headers['X-Auth-Code'] = anonymousSession['access_token']
        }
      }
    }

    if (partnerId) {
      headers['X-Preferred-Partner-Id'] = partnerId
    }

    const response =
      method === 'delete'
        ? await targetApi.delete(endpoint, { data: requestData, headers, signal, timeout })
        : method === 'get'
          ? await targetApi.get(endpoint, { headers, cancelToken, timeout })
          : await targetApi[method](endpoint, requestData, { headers, cancelToken, timeout })
    if (response.status < 200 || response.status >= 300) {
      throw new Error(response.data.message)
    }
    if (verbose && process.env.NODE_ENV === 'development') {
      console.log(`[${method.toUpperCase()}] ${endpoint}`, response.data)
    }

    return {
      data: response.data,
      status: response.status,
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Request canceled', error.message)
      return { error: error.message, status: null, cancelled: true }
    } else {
      console.error(error)
      return { error, status: error.response?.status }
    }
  }
}
